import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { jwtDecode } from "jwt-decode";
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { SmartAuthDecryptedData } from '../../features/auth/login/models/data.model';
import { LoginRequest } from '../../features/auth/login/models/login-request.model';
import { SmartAuthLoginApiResponse } from '../../features/auth/login/models/login-response.model';
import { AuthEndpoints } from '../../features/teacher/availability/api/auth.endpoint';
import { ApiConfigService } from '../api/api-config.service';
import { CryptoService } from './crypto.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private http = inject(HttpClient);
    private api = inject(ApiConfigService);
    private crypto = inject(CryptoService);
    private platformId = inject(PLATFORM_ID);

    private readonly DATA_KEY = 'access_token';
    private readonly PROFILE_KEY = 'profile';
    private readonly _isAuthenticated$ = new BehaviorSubject<boolean>(this.hasValidToken());
    readonly isAuthenticated$ = this._isAuthenticated$.asObservable();

    hasValidToken(): boolean {
        const token = this.getAccessToken();
        return !!token && !this.isTokenExpired();
    }
    private isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    private readStorage(key: string): string | null {
        if (!this.isBrowser()) return null;
        return localStorage.getItem(key);
    }

    private writeStorage(key: string, value: string): void {
        if (!this.isBrowser()) return;
        localStorage.setItem(key, value);
    }

    private removeStorage(key: string): void {
        if (!this.isBrowser()) return;
        localStorage.removeItem(key);
    }

    getAccessToken(): string | null {
        return this.readStorage(this.DATA_KEY);
    }

    isTokenExpired(bufferSeconds: number = 30): boolean {
        const token = this.getAccessToken();

        if (!token) return true;

        try {
            const decoded: any = jwtDecode(token);

            if (!decoded?.exp) return true;

            const now = Math.floor(Date.now() / 1000);
            return decoded.exp < now + bufferSeconds;
        } catch {
            return true;
        }
    }

    getProfile(): SmartAuthDecryptedData | null {
        const profile = this.readStorage(this.PROFILE_KEY);
        if (!profile) return null;
        try {
            return JSON.parse(profile);
        } catch (e) {
            return null;
        }
    }

    async login(data: LoginRequest): Promise<{ token: string; profile: SmartAuthDecryptedData | null }> {
        const url = this.api.auth(AuthEndpoints.login);

        const res = await firstValueFrom(
            this.http.post<SmartAuthLoginApiResponse>(url, data)
        );
        if (!res?.success || !res.token) {
            throw new Error('Login inválido');
        }

        this.writeStorage(this.DATA_KEY, res.token);
        const profile = res.data ? this.crypto.decrypt(res.data) : null;
        this.writeStorage(this.PROFILE_KEY, JSON.stringify(profile));
        this._isAuthenticated$.next(true);
        return { token: res.token, profile };
    }

    logout(): void {
        this.removeStorage(this.DATA_KEY);
        this.removeStorage(this.PROFILE_KEY);
        this._isAuthenticated$.next(false);
    }
}